import React, {Component} from 'react';
import PropTypes from "prop-types";
import Select from "react-select";
import MachineSelector from "../../globalComponents/MachineSelector";
import InputDatePicker from "../../utils/InputDatePicker";
import Api from "../../utils/Api";
import Machines from "../../utils/entities/Machines";
import Timeformat from "../../utils/Timeformat";

class RawDataContainer extends Component {

    static propTypes = {
        baseData: PropTypes.object.isRequired,
        machines: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);

        this.api = new Api();

        const currentCompanyId = (this.props.baseData.companies && this.props.baseData.companies.currentCompany) ? this.props.baseData.companies.currentCompany.id : 0;
        let timezone = 'Europe/Berlin';
        if (this.props.baseData.companies) {
            const company = this.props.baseData.companies.companies.find(c => c.id === currentCompanyId);
            if (company && company.timezone) {
                timezone = company.timezone;
            }
        }

        this.state = {
            currentCompanyId: currentCompanyId,
            currentMachine: null,
            dataType: 'status',
            showLatest500: true,
            selectedDate: new Date(),
            selectedHour: 0,
            machines: this.props.machines,
            errorMessage: '',
            rawData: [],
            showResults: false,
            timezone: timezone,
            loading: false
        };

        this.translate = this.props.baseData.translate;

        this.onCompanySelected = this.onCompanySelected.bind(this);
        this.onMachineSelected = this.onMachineSelected.bind(this);
        this.onDataTypeChanged = this.onDataTypeChanged.bind(this);
        this.onCheckboxChange = this.onCheckboxChange.bind(this);
        this.onDateChange = this.onDateChange.bind(this);
        this.onHourChange = this.onHourChange.bind(this);
        this.onShowRecordsClicked = this.onShowRecordsClicked.bind(this);
        this.requestMachines = this.requestMachines.bind(this);
    }

    componentDidMount() {
        if (this.state.currentCompanyId !== 0) {
            this.requestMachines(this.state.currentCompanyId);
        }
    }

    onCompanySelected(option) {
        const companyId = option ? option.value : 0;
        let timezone = this.state.timezone;

        if (this.props.baseData.companies) {
            const company = this.props.baseData.companies.companies.find(c => c.id === companyId);
            if (company && company.timezone) {
                timezone = company.timezone;
            }
        }

        this.setState({
            currentCompanyId: companyId,
            currentMachine: null,
            timezone: timezone
        }, () => {
            if (companyId !== 0) {
                this.requestMachines(companyId);
            }
        });
    }

    requestMachines(idCompany) {
        const params = {
            id_company: idCompany,
            id_user: this.props.baseData.userData.id,
            pastTimeOfInactivity: 0,
        };

        const callback = function(res) {
            if (res) {
                this.setState({machines: new Machines(res)});
            }
        }.bind(this);

        this.api.request('GetMachines', params, callback, null);
    }

    onMachineSelected(machine) {
        this.setState({
            currentMachine: machine,
            errorMessage: '',
            showResults: false,
            rawData: []
        });
    }

    onDataTypeChanged(e) {
        this.setState({
            dataType: e.target.value,
            rawData: [],
            showResults: false,
            errorMessage: ''
        });
    }

    onCheckboxChange(e) {
        this.setState({
            showLatest500: e.target.checked
        });
    }

    onDateChange(event, name, date) {
        this.setState({
            selectedDate: date
        });
    }

    onHourChange(e) {
        this.setState({
            selectedHour: parseInt(e.target.value)
        });
    }
    
    onShowRecordsClicked() {
        if (!this.state.currentMachine || this.state.currentMachine.value === 'all') {
            this.setState({errorMessage: this.translate('pleaseSelectAmachine'), rawData: []});
            return;
        }

        this.setState({loading: true, rawData: [], showResults: true});

        const year = this.state.selectedDate.getFullYear();
        const month = (this.state.selectedDate.getMonth() + 1).toString().padStart(2, '0');
        const day = this.state.selectedDate.getDate().toString().padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const actionMap = {
            'status': 'getRawDataStatus',
            'stick': 'getRawDataStitches',
            'threadbreak': 'getRawDataThreadbreaks',
            'logStatus': 'getRawDataStatusLogs'
        };

        const params = {
            idCompany: this.state.currentCompanyId,
            idMachine: this.state.currentMachine ? this.state.currentMachine.value : null,
            showLatest500: this.state.showLatest500,
            selectedDate: dateString,
            selectedHour: this.state.selectedHour,
            action: actionMap[this.state.dataType] || 'getRawDataStatus'
        };
        
        const callback = function(res) {
            if (res && res.success) {
                this.setState({
                    rawData: res.chartData || [],
                    timezone: res.timezone || 'Europe/Berlin',
                    loading: false
                });
            } else {
                this.setState({
                    errorMessage: res ? res.message : 'Error loading raw data',
                    loading: false
                });
            }
        }.bind(this);
        
        this.api.request('AdminFunctions', params, callback, null);
    }

    getCompanyOptions() {
        const companies = this.props.baseData.companies ? this.props.baseData.companies.getAllCompanies('name', 'asc') : [];
        return companies.map(company => ({
            value: company.id,
            label: company.name
        }));
    }

    renderTable() {

        if (!this.state.showResults || this.state.loading) {
            return null;
        }

        if (this.state.rawData.length === 0) {
            return (
                <div style={{marginTop: '30px', textAlign: 'center', fontStyle: 'italic'}}>
                    {this.translate('noResultsFound')}
                </div>
            );
        }

        const isStickData = this.state.dataType === 'stick';
        const isThreadbreakData = this.state.dataType === 'threadbreak';
        const isLogStatusData = this.state.dataType === 'logStatus';

        return (
            <div style={{marginTop: '30px'}}>
                <table className="table table-striped table-bordered">
                    <thead>
                        {isStickData ? (
                            <tr>
                                <th>{this.translate('time')}</th>
                                <th>{this.translate('stitches')} (high - low)</th>
                                <th>{this.translate('stitchCounterLow')} </th>
                                <th>{this.translate('stitchCounterHigh')} </th>
                                <th>{this.translate('threadbreaks')}</th>
                                <th>{this.translate('activeHeads')}</th>
                            </tr>
                        ) : isThreadbreakData ? (
                            <tr>
                                <th>{this.translate('time')}</th>
                                <th>{this.translate('needle')}</th>
                                <th>{this.translate('head')}</th>
                                <th>{this.translate('pattern')}</th>
                                <th>{this.translate('stitches')}</th>
                            </tr>
                        ) : isLogStatusData ? (
                            <tr>
                                <th>{this.translate('time')}</th>
                                <th>{this.translate('timestampMachine')}</th>
                                <th>Code</th>
                                <th>{this.translate('stitches')}</th>
                                <th>{this.translate('rotationSpeed')}</th>
                                <th>{this.translate('operatorId')}</th>
                                <th>{this.translate('pattern')}</th>
                                <th>{this.translate('passesPattern')}</th>
                                <th>{this.translate('repeatedPattern')}</th>
                                <th>{this.translate('patternStitchcounter')}</th>
                                <th>{this.translate('stitches')} (M)</th>
                                <th>{this.translate('activeNeedle')}</th>
                                <th>{this.translate('headType')}</th>
                                <th>{this.translate('shouldRepeatPattern')}</th>
                                <th>{this.translate('stitchType')}</th>
                                <th>{this.translate('activeUser')}</th>
                                <th>{this.translate('activeRfid')}</th>
                                <th>{this.translate('activeHeads')}</th>
                            </tr>
                        ) : (
                            <tr>
                                <th>{this.translate('startTime')}</th>
                                <th>{this.translate('endTime')}</th>
                                <th>{this.translate('status')}</th>
                                <th>{this.translate('pattern')} / {this.translate('stitches')}</th>
                            </tr>
                        )}
                    </thead>
                    <tbody style={{overflow: 'auto'}}>
                        {this.state.rawData.map((row, index) => {
                            if (isStickData) {
                                const time = Timeformat.fromUnix(row.timestamp);
                                const formattedTime = time ? time.getDateTimeWithHundredths(this.state.timezone) : '-';

                                return (
                                    <tr key={index}>
                                        <td>{formattedTime}</td>
                                        <td>{row.stitch_counter}</td>
                                        <td>{row.stitch_counter_low}</td>
                                        <td>{row.stitch_counter_high}</td>
                                        <td>{row.threadbreak_counter}</td>
                                        <td>{row.active_heads}</td>
                                    </tr>
                                );
                            } else if (isThreadbreakData) {
                                const time = Timeformat.fromUnix(row.timestamp);
                                const formattedTime = time ? time.getDateTimeWithHundredths(this.state.timezone) : '-';

                                return (
                                    <tr key={index}>
                                        <td>{formattedTime}</td>
                                        <td>{row.needle}</td>
                                        <td>{row.head}</td>
                                        <td>{row.pattern}</td>
                                        <td>{row.pattern_stitchnumber}</td>
                                    </tr>
                                );
                            } else if (isLogStatusData) {
                                const time = Timeformat.fromUnix(row.timestamp);
                                const formattedTime = time ? time.getDateTimeWithHundredths(this.state.timezone) : '-';
                                
                                const mTime = Timeformat.fromUnix(row.timestamp_machine);
                                const formattedMTime = mTime ? mTime.getDateTimeWithHundredths(this.state.timezone) : '-';

                                return (
                                    <tr key={index}>
                                        <td>{formattedTime}</td>
                                        <td>{formattedMTime}</td>
                                        <td>{row.status_code}</td>
                                        <td>{row.stitch_counter}</td>
                                        <td>{row.rotation_speed}</td>
                                        <td>{row.operator_id}</td>
                                        <td>{row.pattern_id}</td>
                                        <td>{row.passes_pattern}</td>
                                        <td>{row.repeated_pattern}</td>
                                        <td>{row.pattern_stitchcounter}</td>
                                        <td>{row.pattern_stitchnumber}</td>
                                        <td>{row.active_needle}</td>
                                        <td>{row.head_type}</td>
                                        <td>{row.should_repeatpattern}</td>
                                        <td>{row.stitch_type}</td>
                                        <td>{row.active_user}</td>
                                        <td>{row.active_rfid}</td>
                                        <td>{row.active_heads}</td>
                                    </tr>
                                );
                            } else {
                                const startTime = Timeformat.fromUnix(row.start);
                                const stopTime = Timeformat.fromUnix(row.stop);
                                const formattedStart = startTime ? startTime.getDateTimeWithHundredths(this.state.timezone) : '-';
                                const formattedStop = stopTime ? stopTime.getDateTimeWithHundredths(this.state.timezone) : '-';
                                const patternStitches = (row.id_pattern || '-') + ' / ' + (row.stitches || '0');

                                return (
                                    <tr key={index}>
                                        <td>{formattedStart}</td>
                                        <td>{formattedStop}</td>
                                        <td>{row.statusText}</td>
                                        <td>{patternStitches}</td>
                                    </tr>
                                );
                            }
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const hours = [];
        for (let i = 0; i < 24; i++) {
            hours.push(i);
        }

        const companyOptions = this.getCompanyOptions();
        const selectedCompany = companyOptions.find(o => o.value === this.state.currentCompanyId);

        const fieldStyle = {width: '250px', marginBottom: '15px', marginRight: '15px'};

        return (
            <div className="rawDataContainer">
                <nav>
                    <h1>{this.translate('rawData')}</h1>
                </nav>
                <div className="contentarea">
                    <div style={{display: 'flex', flexWrap: 'wrap'}}>
                        <div style={fieldStyle}>
                            <label style={{display: 'block'}}>{this.translate('company')}</label>
                            <Select
                                name="company-selector"
                                className="chosen-container chosen-container-single"
                                options={companyOptions}
                                value={selectedCompany}
                                onChange={this.onCompanySelected}
                                placeholder={this.translate('select') + '...'}
                                clearable={false}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={{display: 'block'}}>{this.translate('machine')}</label>
                            <MachineSelector
                                translate={this.translate}
                                machine={this.state.machines}
                                currentCompany={this.state.currentCompanyId}
                                onMachineSelected={this.onMachineSelected}
                                currentMachine={this.state.currentMachine}
                                addAllOption={true}
                                disabled={parseInt(this.state.currentCompanyId) === 0}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={{display: 'block'}}>{this.translate('dataType') || 'Datentyp'}</label>
                            <select
                                className="form-control"
                                value={this.state.dataType}
                                onChange={this.onDataTypeChanged}
                            >
                                <option value="status">{this.translate('statusData') || 'Statusdaten'}</option>
                                <option value="stick">{this.translate('stickData') || 'Stickdaten'}</option>
                                <option value="threadbreak">{this.translate('threadbreaks') || 'Fadenbrüche'}</option>
                                <option value="logStatus">{this.translate('statusLogs') || 'Maschinenstatus Rohdaten'}</option>
                            </select>
                            {this.state.dataType === 'logStatus' && (
                                <div style={{fontStyle: 'italic', fontSize: 'small', marginTop: '5px'}}>
                                    {this.translate('rawDataArchivedNote')}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="checkbox" style={{marginBottom: '15px'}}>
                        <label>
                            <input
                                type="checkbox"
                                checked={this.state.showLatest500}
                                onChange={this.onCheckboxChange}
                            />
                            {this.translate('showLatest500Records')}
                        </label>
                    </div>
                    <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end'}}>
                        <div style={fieldStyle}>
                            <label style={{display: 'block'}}>{this.translate('date')}</label>
                            <InputDatePicker
                                name="selectedDate"
                                value={this.state.selectedDate}
                                onChange={this.onDateChange}
                                disabled={this.state.showLatest500}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label style={{display: 'block'}}>{this.translate('hour')}</label>
                            <select
                                className="form-control"
                                value={this.state.selectedHour}
                                onChange={this.onHourChange}
                                disabled={this.state.showLatest500}
                            >
                                <option value="-1">{this.translate('fullDay') || 'Ganzer Tag'}</option>
                                {hours.map(hour => (
                                    <option key={hour} value={hour}>
                                        {hour.toString().padStart(2, '0')}:00 - {hour.toString().padStart(2, '0')}:59
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{marginBottom: '15px', height: '38px', display: 'flex', alignItems: 'center'}}>
                            <span className="text-muted">({this.state.timezone})</span>
                        </div>
                    </div>
                    <div style={{marginTop: '20px'}}>
                        {this.state.errorMessage && (
                            <div style={{color: 'red', marginBottom: '10px'}}>
                                {this.state.errorMessage}
                            </div>
                        )}
                        <button className="btn btn-primary" onClick={this.onShowRecordsClicked} style={{width: '250px'}} disabled={this.state.loading}>
                            {this.state.loading ? this.translate('loading') + '...' : this.translate('showRecords')}
                        </button>
                    </div>
                    {this.renderTable()}
                </div>
            </div>
        );
    }
}

export default RawDataContainer;
